import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { treatments as treatmentList } from "@/data/treatments";

const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Il nome è obbligatorio")
    .max(100, "Il nome deve essere meno di 100 caratteri"),
  phone: z
    .string()
    .trim()
    .min(1, "Il telefono è obbligatorio")
    .regex(/^[0-9+\s()-]{6,20}$/, "Numero di telefono non valido"),
  treatment: z.string().min(1, "Seleziona un trattamento"),
  message: z
    .string()
    .trim()
    .min(10, "Il messaggio deve essere almeno 10 caratteri")
    .max(1000, "Il messaggio deve essere meno di 1000 caratteri"),
  consent: z.boolean().refine((v) => v === true, {
    message: "Devi acconsentire al trattamento dei dati per essere ricontattato",
  }),
});

type ContactFormData = z.infer<typeof contactSchema>;

const treatments = [...treatmentList.map((t) => t.name), "Altro"];

interface TreatmentFormProps {
  preselectedTreatment?: string;
}

const TreatmentForm = ({ preselectedTreatment }: TreatmentFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      phone: "",
      treatment: preselectedTreatment || "",
      message: "",
      consent: false,
    },
  });

  const onSubmit = (data: ContactFormData) => {
    setIsSubmitting(true);

    const message = `Ciao! Sono ${data.name}

Telefono: ${data.phone}
Trattamento: ${data.treatment}

Messaggio:
${data.message}`;

    const whatsappUrl = `https://wa.me/393461533000?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");

    toast.success("Richiesta inviata!", {
      description: "Ti ricontatterò al più presto.",
    });

    form.reset();
    setIsSubmitting(false);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
      <h3 className="font-serif text-2xl text-primary mb-2">Richiedi informazioni</h3>
      <p className="text-muted-foreground mb-6">
        Compila il form per essere ricontattato su WhatsApp.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome *</FormLabel>
                <FormControl>
                  <Input placeholder="Il tuo nome" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefono *</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+39 123 456 7890" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="treatment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Trattamento *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona un trattamento" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-background border-border max-h-[300px]">
                    {treatments.map((treatment) => (
                      <SelectItem key={treatment} value={treatment}>
                        {treatment}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Messaggio *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Scrivi qui il tuo messaggio..."
                    className="min-h-[120px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="consent"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start gap-3 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal text-muted-foreground">
                    Ho letto e accetto la{" "}
                    <Link to="/privacy" className="text-primary underline hover:text-accent">
                      Privacy Policy
                    </Link>{" "}
                    e acconsento a essere ricontattato. *
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full rounded-full" disabled={isSubmitting}>
            <Send className="w-4 h-4 mr-2" />
            {isSubmitting ? "Invio in corso..." : "Invia richiesta"}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default TreatmentForm;
